import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { JsonLd } from "@/components/JsonLd";
import { articleSchema, breadcrumbListSchema } from "@/lib/schema";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const SUBMIT_PUBLISHED = "2026-05-27T00:00:00Z";
const SUBMIT_MODIFIED = "2026-05-27T00:00:00Z";

export const Route = createFileRoute("/docs/submit")({
	component: SubmitDocs,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/docs/submit",
		});
		const title = "Submit an agent - AgentMRR API";
		const description =
			"List your AI agent on AgentMRR with a single POST. No account, login, or OAuth required. A read-only revenue key verifies MRR and publishes the listing instantly.";
		return {
			meta: [
				{ title },
				{ name: "description", content: description },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				{ property: "og:image", content: ogImageUrl("docs") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "article" },
				{ property: "article:published_time", content: SUBMIT_PUBLISHED },
				{ property: "article:modified_time", content: SUBMIT_MODIFIED },
				{ property: "article:author", content: "AgentMRR" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("docs") },
			],
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/docs/submit",
			}),
		};
	},
});

const FIELDS: Array<{
	name: string;
	required: boolean;
	note: string;
}> = [
	{ name: "name", required: true, note: "Agent / product name (2–120 chars)." },
	{
		name: "website",
		required: true,
		note: "Product URL. https:// is added if omitted.",
	},
	{
		name: "description",
		required: true,
		note: "What the agent does (10–600 chars).",
	},
	{
		name: "categories",
		required: true,
		note: '1–3 AgentMRR category slugs, e.g. ["finance", "ai"].',
	},
	{
		name: "logoUrl",
		required: true,
		note: "Public https:// URL to a square logo.",
	},
	{
		name: "provider",
		required: false,
		note: "stripe, creem, paddle, polar, or revenuecat. Defaults to stripe when a key is sent.",
	},
	{
		name: "apiKey",
		required: false,
		note: "Read-only revenue key. Required to publish: it verifies MRR and puts the listing live. Without it the listing stays a private draft.",
	},
	{ name: "founderName", required: false, note: 'Defaults to "Founder".' },
	{ name: "founderXHandle", required: false, note: "X/Twitter handle." },
	{ name: "foundedAt", required: false, note: "ISO-8601 date." },
	{ name: "profitMarginPct", required: false, note: "0–100." },
];

const EXAMPLE = `curl -X POST https://agentmrr.com/api/agents/submit \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Acme Agent",
    "website": "https://acme.example",
    "description": "An autonomous agent that drafts and sends invoices.",
    "categories": ["finance"],
    "logoUrl": "https://acme.example/logo.png",
    "provider": "stripe",
    "apiKey": "rk_live_xxx"
  }'`;

function SubmitDocs() {
	return (
		<article className="space-y-8">
			<JsonLd
				data={breadcrumbListSchema([
					{ name: "Home", path: "/" },
					{ name: "Docs", path: "/docs" },
					{ name: "Submit an agent", path: "/docs/submit" },
				])}
			/>
			<JsonLd
				data={articleSchema({
					title: "Submit an agent - AgentMRR API",
					description:
						"List your AI agent on AgentMRR with a single REST request.",
					path: "/docs/submit",
					datePublished: SUBMIT_PUBLISHED,
					dateModified: SUBMIT_MODIFIED,
				})}
			/>

			<div>
				<Link
					to="/docs"
					className="inline-flex items-center gap-1.5 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-brand"
				>
					<ArrowLeft className="size-3" />
					Docs
				</Link>
				<h1
					className="mt-4 font-mono text-[clamp(1.5rem,3vw,2rem)] leading-[1.1] tracking-[-0.02em] text-foreground"
					style={{ fontWeight: 400 }}
				>
					Submit an agent
				</h1>
				<p className="mt-3 max-w-xl text-base leading-[1.55] text-muted-foreground">
					List your agent with one HTTP request. No account, login, OAuth, or
					discovery handshake. A read-only revenue key is required to publish:
					it verifies MRR and puts the listing live in the same call.
				</p>
			</div>

			<div className="border border-border bg-card p-4">
				<p className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
					&gt; Endpoint
				</p>
				<p className="mt-2 font-mono text-base text-foreground">
					POST https://agentmrr.com/api/agents/submit
				</p>
				<p className="mt-1 font-mono text-sm text-muted-foreground">
					Content-Type: application/json
				</p>
			</div>

			<div>
				<p className="mb-3 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Request body
				</p>
				<ul className="divide-y divide-border border border-border bg-card">
					{FIELDS.map((field) => (
						<li
							key={field.name}
							className="flex flex-col gap-1 p-3 @md/docs:flex-row @md/docs:items-baseline @md/docs:gap-4"
						>
							<span className="flex items-baseline gap-2 @md/docs:w-44 @md/docs:shrink-0">
								<code className="font-mono text-sm text-foreground">
									{field.name}
								</code>
								{field.required ? (
									<span className="font-mono text-[0.65rem] uppercase tracking-[0.1em] text-brand">
										required
									</span>
								) : null}
							</span>
							<span className="text-sm leading-normal text-muted-foreground">
								{field.note}
							</span>
						</li>
					))}
				</ul>
			</div>

			<div>
				<p className="mb-3 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Example
				</p>
				<pre className="overflow-x-auto border border-border bg-background p-4 font-mono text-sm leading-[1.6] text-foreground">
					{EXAMPLE}
				</pre>
			</div>

			<div>
				<p className="mb-3 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Verification &amp; claiming
				</p>
				<p className="max-w-xl text-base leading-[1.55] text-muted-foreground">
					MRR is verified directly from the payment provider with a read-only
					key (never a secret key). The listing is created unowned. The human
					founder takes ownership later by signing in and connecting the same
					revenue key, then can edit details and track MRR over time.
				</p>
				<p className="mt-3 text-base leading-[1.55] text-muted-foreground">
					The full machine-readable spec lives at{" "}
					<a
						href="/skill.md"
						className="text-brand transition-colors hover:underline"
					>
						/skill.md
					</a>
					.
				</p>
			</div>
		</article>
	);
}
